import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { escapeClosesTopmost, openModalLayer } from './modalStack';
import styles from './Modal.module.css';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

export function Modal({ isOpen, onClose, title, children, footer, size = 'md' }: ModalProps) {
  // The latest onClose, read when Escape is pressed, so the layer below is
  // stacked once per opening: a parent passing a new onClose each render
  // never moves this Modal above one opened after it.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  // Each open Modal its own title id: a Modal over another never shares one.
  const titleId = useId();

  // Close on Escape, only the topmost open Modal (a dialog over a form closes
  // alone, the form and its edits stay), and the page stays locked until the
  // last one closes (modalStack.ts).
  useEffect(() => {
    if (!isOpen) return;
    const layer = openModalLayer(document.body);
    const onKey = escapeClosesTopmost(layer, () => onCloseRef.current());
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      layer.release();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Portal to document.body so the fixed-position backdrop resolves
  // against the VIEWPORT, not the nearest ancestor that happens to
  // establish a containing block for fixed descendants. Several
  // consumers mount Modal inside the nav chrome — UserChipMenu and
  // MobileNavOverlay both render the logout confirm there — and that
  // chrome carries backdrop-filter (and, on the mobile sheet, a
  // transform), each of which traps `position: fixed` so the backdrop
  // collapsed onto the 64px nav bar at the top of the screen with no
  // viewport dimming. createPortal lifts the whole overlay out of
  // that subtree; React keeps synthetic events bubbling through the
  // React tree, so onClose / stopPropagation behave unchanged.
  return createPortal(
    <div className={styles.backdrop} onClick={onClose} role="presentation">
      <div
        className={`${styles.modal} ${styles[`size_${size}`]}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className={styles.header}>
          <h2 id={titleId} className={styles.title}>{title}</h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </header>
        <div className={styles.body}>{children}</div>
        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
